/* Rare Hills city search. No app, external API or homepage data download. */
(() => {
  'use strict';
  const PAGE_SIZE = 12;
  const FILTER_KEYS = ['property_type', 'amenities', 'duration', 'highlights', 'inclusions', 'clients'];
  const FILTER_LABELS = { property_type: 'Property type', amenities: 'Amenities', duration: 'Trip duration', highlights: 'Highlights', inclusions: 'What’s included', clients: 'Brands & clients' };
  const SORTS = ['recommended', 'title-ascending', 'title-descending', 'created-descending', 'created-ascending', 'price-ascending', 'price-descending'];
  const MAX_PAGES = 100; // Shopify permits access to the first 25,000 collection products.
  const cache = new Map();
  const ignoredNativeFilters = new Set(['filter.v.availability', 'filter.p.availability']);
  const isAvailabilityParam = (name) => ignoredNativeFilters.has(String(name).toLowerCase()) || /^filter\.(?:v|p)\.availability(?:$|\[)/i.test(name);
  const removeAvailability = (url) => {
    const keys = [...url.searchParams.keys()].filter(isAvailabilityParam);
    keys.forEach((key) => url.searchParams.delete(key));
    return keys.length > 0;
  };

  const moduleNames = { coworking: 'Coworking spaces', office: 'Office spaces', 'virtual-office': 'Virtual offices', coliving: 'Coliving & PG', trip: 'Trips', hotel: 'Hotels', general: 'Other listings' };
  const moduleAliases = { 'coworking-space': 'coworking', 'coworking-spaces': 'coworking', 'co-working': 'coworking', 'co-working-space': 'coworking', 'office-space': 'office', 'office-spaces': 'office', 'private-office': 'office', 'private-offices': 'office', 'virtual-offices': 'virtual-office', 'co-living': 'coliving', 'coliving-pg': 'coliving', 'coliving-&-pg': 'coliving', pg: 'coliving', trips: 'trip', hotels: 'hotel', directory: 'general', 'other-listings': 'general', 'directory-listing': 'general', 'directory-listings': 'general' };
  const aliases = new Map([
    ['gurgaon', 'gurugram'], ['gurugram', 'gurugram'],
    ['bangalore', 'bengaluru'], ['bengaluru', 'bengaluru'],
    ['delhi', 'delhi'], ['new delhi', 'delhi']
  ]);
  const normalize = (value) => String(value || '').normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase().replace(/\s+/g, ' ').trim();
  const moduleKey = (value) => {
    const key = normalize(value).replace(/[ _]+/g, '-');
    return moduleAliases[key] || key || 'general';
  };
  const cityKey = (value) => {
    const key = normalize(value);
    return aliases.get(key) || aliases.get(key.split(',')[0].trim()) || key;
  };
  const element = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = String(text);
    return node;
  };
  const safeURL = (value, sameOrigin = false) => {
    if (!value || typeof value !== 'string') return null;
    try {
      const url = new URL(value, window.location.origin);
      if (!['https:', 'http:'].includes(url.protocol)) return null;
      if (sameOrigin && url.origin !== window.location.origin) return null;
      return url;
    } catch { return null; }
  };
  const link = (className, text, href) => {
    const node = element('a', className, text);
    node.href = href;
    return node;
  };
  const arrow = () => {
    const node = element('span', '', '↗');
    node.setAttribute('aria-hidden', 'true');
    return node;
  };
  function card(item, root, eager) {
    const href = safeURL(item.url, true)?.href;
    if (!href) return null;
    const article = element('article', 'listing-card');
    const photoLink = link('listing-card__image', '', href);
    photoLink.setAttribute('aria-label', `View ${item.title}`);
    const imageURL = safeURL(item.image);
    if (imageURL) {
      const photo = element('img');
      photo.src = imageURL.href;
      photo.alt = item.imageAlt || item.title || '';
      photo.width = Number(item.imageWidth) || 720;
      photo.height = Number(item.imageHeight) || 540;
      photo.loading = eager ? 'eager' : 'lazy';
      photo.decoding = 'async';
      photoLink.append(photo);
    } else {
      const placeholder = element('span', 'listing-card__placeholder', 'Photo coming soon');
      photoLink.append(placeholder);
    }
    if (item.badge) photoLink.append(element('span', 'listing-card__badge', item.badge));
    const body = element('div', 'listing-card__body');
    const heading = element('h3', 'listing-card__title');
    heading.append(link('', item.title, href));
    body.append(heading);

    const bottom = element('div', 'listing-card__bottom');
    if (item.price) {
      const price = element('p', 'listing-card__price');
      if (item.priceFrom) price.append(element('span', 'listing-card__from', 'From '));
      price.append(document.createTextNode(item.price));
      if (item.suffix) price.append(document.createTextNode(' '), element('span', 'listing-card__suffix', item.suffix));
      bottom.append(price);
    }
    const actions = element('div', 'listing-card__actions');
    const view = link('listing-card__view', 'View ', href);
    view.setAttribute('aria-label', `View ${item.title}`);
    const viewIcon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    viewIcon.setAttribute('viewBox', '0 0 20 20');
    viewIcon.setAttribute('width', '16');
    viewIcon.setAttribute('height', '16');
    viewIcon.setAttribute('fill', 'none');
    viewIcon.setAttribute('aria-hidden', 'true');
    const viewPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    viewPath.setAttribute('d', 'M4 10h12m-5-5 5 5-5 5');
    viewPath.setAttribute('stroke', 'currentColor');
    viewPath.setAttribute('stroke-width', '1.6');
    viewPath.setAttribute('stroke-linecap', 'round');
    viewPath.setAttribute('stroke-linejoin', 'round');
    viewIcon.append(viewPath);
    view.append(viewIcon);
    actions.append(view);
    const enquiryValue = window.RareHills?.enquiryURL?.(item.enquiry) || '';
    const enquiryURL = safeURL(enquiryValue);
    if (enquiryURL) {
      const enquiry = link('button listing-card__enquire', 'Get quote ', enquiryURL.href);
      enquiry.dataset.enquiryUrl = enquiryURL.href;
      enquiry.dataset.listingId = item.id;
      enquiry.dataset.listingTitle = item.title;
      enquiry.dataset.enquiryContext = root.dataset.enquiryContext || 'false';
      enquiry.setAttribute('aria-haspopup', 'dialog');
      enquiry.append(arrow());
      actions.append(enquiry);
    } else {
      const contact = safeURL(root.dataset.contactUrl);
      if (contact) {
        const contactLink = link('button listing-card__enquire', 'Get quote ', contact.href);
        contactLink.append(arrow());
        actions.append(contactLink);
      } else {
        const unavailable = element('button', 'listing-card__enquire', 'Get quote');
        unavailable.type = 'button';
        unavailable.disabled = true;
        unavailable.title = 'Quotes are currently unavailable';
        actions.append(unavailable);
      }
    }
    bottom.append(actions);
    body.append(bottom);
    article.append(photoLink, body);
    return article;
  }

  class CityResults {
    constructor(root) {
      this.root = root;
      this.native = root.querySelector('[data-city-native]');
      this.panel = root.querySelector('[data-city-panel]');
      this.grid = root.querySelector('[data-city-grid]');
      this.status = root.querySelector('[data-city-status]');
      this.sort = root.querySelector('[data-city-sort-input]');
      this.sortWrap = root.querySelector('[data-city-sort]');
      this.retry = root.querySelector('[data-city-retry]');
      this.empty = root.querySelector('[data-city-empty]');
      this.pagination = root.querySelector('[data-city-pagination]');
      this.collectionURL = safeURL(root.dataset.collectionUrl, true);
      this.heading = root.querySelector('[data-city-heading]');
      this.breadcrumb = root.querySelector('[data-city-breadcrumb]');
      this.originalTitle = root.dataset.collectionTitle || this.heading?.textContent || 'listings';
      this.records = null;
      this.complete = false;
      this.expectedTotal = 0;
      this.controller = null;
      this.requestID = 0;
      this.page = 1;
      this.pageSize = Math.max(6, Math.min(36, Number(root.dataset.pageSize) || PAGE_SIZE));
      this.facetRoot = root.querySelector('[data-directory-facets]');
      this.facetGroups = root.querySelector('[data-facet-groups]');
      this.facetStatus = root.querySelector('[data-facet-status]');
      this.facetLoad = root.querySelector('[data-facet-load]');
      this.facetReset = root.querySelector('[data-facet-reset]');
      this.selectedWrap = root.querySelector('[data-facet-selected]');
      this.filters = {};
      this.facetsRequested = false;
      this.openFacets = new Set();
      try { this.extraFacets = JSON.parse(root.querySelector('[data-facet-config]')?.textContent || '[]').filter((item) => FILTER_KEYS.includes(item.key)); } catch { this.extraFacets = []; }
      const sidebarDetails = root.querySelector('.collection-sidebar__details');
      if (sidebarDetails) {
        if (window.matchMedia('(max-width: 749px)').matches) sidebarDetails.open = false;
        sidebarDetails.dataset.sidebarReady = '';
      }
      this.facetLoad?.addEventListener('click', () => { this.facetsRequested = true; this.native.hidden = true; this.panel.hidden = false; this.load(); });
      this.facetReset?.addEventListener('click', () => { this.filters = {}; this.page = 1; this.saveURL(); this.render(); });
      this.facetGroups?.addEventListener('toggle', (event) => {
        const details = event.target;
        if (!details.matches?.('[data-facet-trigger]') || !details.open || this.facetsRequested) return;
        this.openFacets.add(details.dataset.field);
        this.facetsRequested = true;
        this.native.hidden = true; this.panel.hidden = false; this.load();
      }, true);
      this.facetGroups?.addEventListener('change', (event) => {
        const input = event.target.closest('[data-facet-key]');
        if (!input || input.disabled) return;
        const key = input.dataset.facetKey;
        const selected = new Set(this.filters[key] || []);
        if (input.checked) selected.add(input.value); else selected.delete(input.value);
        this.filters[key] = [...selected]; this.page = 1; this.saveURL(); this.render();
      });
      this.selectedWrap?.addEventListener('click', (event) => {
        const button = event.target.closest('[data-remove-facet]');
        if (!button) return;
        this.filters[button.dataset.removeFacet] = (this.filters[button.dataset.removeFacet] || []).filter((value) => value !== button.dataset.value);
        this.page = 1; this.saveURL(); this.render();
      });
      this.retry?.addEventListener('click', () => this.load(true));
      this.sort?.addEventListener('change', () => { this.page = 1; this.saveURL(); this.render(); });
      this.pagination?.addEventListener('click', (event) => {
        const button = event.target.closest('[data-city-page]');
        if (!button || button.disabled) return;
        this.page = Number(button.dataset.cityPage);
        this.saveURL();
        this.render();
        this.status.focus({ preventScroll: true });
        this.panel.scrollIntoView({ block: 'start', behavior: 'auto' });
      });
      this.sync();
    }
    dispose() { this.requestID += 1; this.controller?.abort(); }
    sync() {
      const params = new URLSearchParams(window.location.search);
      this.city = (params.get('rh_city') || '').trim();
      this.query = (params.get('rh_q') || '').trim();
      this.module = params.has('rh_module') ? moduleKey(params.get('rh_module')) : '';
      this.filters = Object.fromEntries(FILTER_KEYS.map((key) => [key, [...new Set(params.getAll(`rh_f_${key}`).map(normalize).filter(Boolean))]]));
      const hasFacets = Object.values(this.filters).some((values) => values.length);
      const selectedSort = params.get('rh_sort') || 'recommended';
      if (this.sort) this.sort.value = SORTS.includes(selectedSort) ? selectedSort : 'recommended';
      this.page = Math.max(1, parseInt(params.get('rh_page'), 10) || 1);
      if (hasFacets) this.facetsRequested = true;
      const categoryTitle = moduleNames[this.module] || this.originalTitle;
      if (this.heading) this.heading.textContent = categoryTitle;
      if (this.breadcrumb) this.breadcrumb.textContent = categoryTitle;
      const categoryURL = new URL(this.collectionURL || window.location.href);
      categoryURL.search = '';
      if (this.module) categoryURL.searchParams.set('rh_module', this.module);
      if (params.has('preview_theme_id')) categoryURL.searchParams.set('preview_theme_id', params.get('preview_theme_id'));
      this.root.querySelectorAll('[name="rh_category"]').forEach((field) => { field.value = categoryURL.pathname + categoryURL.search; });
      this.root.querySelectorAll('[data-city-clear]').forEach((node) => {
        node.href = categoryURL.pathname + categoryURL.search;
        node.textContent = `${node.hasAttribute('data-city-clear-label') ? 'Clear search · ' : ''}View all ${categoryTitle}`;
      });
      this.root.querySelectorAll('[name="rh_city"]').forEach((field) => { field.value = this.city; });
      this.root.querySelectorAll('[name="rh_q"]').forEach((field) => { field.value = this.query; });
      if (!this.panel || !this.native) return;
      if (!this.city && !this.query && !this.module && !hasFacets && !params.has('rh_sort') && !params.has('rh_page') && !this.facetsRequested) {
        this.dispose();
        this.native.hidden = false;
        this.panel.hidden = true;
        return;
      }
      this.native.hidden = true;
      this.panel.hidden = false;
      this.load();
    }
    async load(force = false) {
      if (!this.collectionURL || !this.panel) return;
      this.native.hidden = true;
      this.panel.hidden = false;
      this.grid.hidden = false;
      this.root.querySelectorAll('script[data-directory-item-list]').forEach((node) => node.remove());
      this.controller?.abort();
      this.controller = new AbortController();
      const signal = this.controller.signal;
      const requestID = ++this.requestID;
      const sourceURL = new URL(this.collectionURL.href);
      sourceURL.search = '';
      const currentParams = new URLSearchParams(window.location.search);
      currentParams.forEach((value, name) => {
        if (!isAvailabilityParam(name) && (name.startsWith('filter.') || name === 'sort_by' || name === 'preview_theme_id')) sourceURL.searchParams.append(name, value);
      });
      sourceURL.searchParams.sort();
      const cacheKey = sourceURL.pathname + sourceURL.search;
      this.retry.hidden = true;
      this.empty.hidden = true;
      this.sortWrap.hidden = true;
      this.pagination.hidden = true;
      this.records = [];
      this.complete = false;
      this.expectedTotal = 0;
      this.grid.replaceChildren();
      this.visibleKeys = null;
      this.panel.setAttribute('aria-busy', 'true');
      this.status.textContent = 'Finding places that match your search…';
      if (this.facetLoad) { this.facetLoad.disabled = true; this.facetLoad.hidden = true; }
      if (this.facetStatus && this.facetsRequested) this.facetStatus.textContent = 'Checking available options across this category…';
      this.facetGroups?.querySelectorAll('input').forEach((input) => { input.disabled = true; });
      try {
        if (!force && cache.has(cacheKey)) {
          this.records = cache.get(cacheKey);
          this.expectedTotal = this.records.length;
        } else {
          let expectedTotal = null;
          let expectedPages = 1;
          const seen = new Set();
          const fetchPage = async (page) => {
            const url = new URL(sourceURL.href);
            url.searchParams.set('view', 'city-data');
            url.searchParams.set('page', String(page));
            let timedOut = false;
            const timer = window.setTimeout(() => { timedOut = true; if (requestID === this.requestID) this.controller.abort(); }, 20000);
            let payload;
            try {
              const response = await fetch(url.href, { signal, credentials: 'same-origin', headers: { Accept: 'application/json' } });
              if (!response.ok) throw new Error('unavailable');
              payload = await response.json();
            } catch (error) {
              if (timedOut) throw new Error('timeout');
              throw error;
            } finally {
              window.clearTimeout(timer);
            }
            if (requestID !== this.requestID || !this.root.isConnected) throw new DOMException('Superseded', 'AbortError');
            if (!Array.isArray(payload.records) || !Number.isInteger(payload.total) || payload.total < 0 || payload.page !== page || !Number.isInteger(payload.pages) || payload.pages < 0 || (payload.pages === 0 && payload.total > 0)) throw new Error('invalid');
            if (payload.limitExceeded || payload.total > 25000 || payload.pages > MAX_PAGES) throw new Error('limit');
            return payload;
          };
          const appendPage = (payload) => {
            if (expectedTotal === null) {
              expectedTotal = payload.total;
              expectedPages = Math.max(1, payload.pages);
              this.expectedTotal = expectedTotal;
            } else if (expectedTotal !== payload.total || expectedPages !== Math.max(1, payload.pages)) {
              throw new Error('changed');
            }
            const valid = payload.records.filter((item) => {
              if (!item || typeof item.id !== 'string' || !item.id || typeof item.title !== 'string' || !safeURL(item.url, true) || seen.has(item.id)) return false;
              seen.add(item.id);
              return true;
            });
            if (valid.length !== payload.records.length) throw new Error('changed');
            this.records.push(...valid);
          };
          // Show first-page matches immediately, then fetch at most three later pages at once.
          // Append batches in collection order even when network responses arrive out of order.
          appendPage(await fetchPage(1));
          if (expectedPages > 1) this.render();
          for (let start = 2; start <= expectedPages; start += 3) {
            const pages = Array.from({ length: Math.min(3, expectedPages - start + 1) }, (_, offset) => start + offset);
            const payloads = await Promise.all(pages.map(fetchPage));
            if (requestID !== this.requestID || !this.root.isConnected) return;
            payloads.forEach(appendPage);
            if (start + pages.length <= expectedPages) this.render();
          }
          if (this.records.length !== expectedTotal) throw new Error('changed');
          cache.set(cacheKey, this.records);
          if (cache.size > 4) cache.delete(cache.keys().next().value);
        }
        if (requestID !== this.requestID || !this.root.isConnected) return;
        this.complete = true;
        this.panel.setAttribute('aria-busy', 'false');
        this.render();
      } catch (error) {
        if (requestID === this.requestID) this.controller?.abort();
        if (error.name === 'AbortError' || requestID !== this.requestID || !this.root.isConnected) return;
        this.panel.setAttribute('aria-busy', 'false');
        // Keep the server-rendered collection usable when the custom data request fails.
        // Never present a partial scan as complete city/search/filter results.
        this.records = null;
        this.complete = false;
        this.grid.replaceChildren();
        this.grid.hidden = true;
        this.native.hidden = false;
        this.sortWrap.hidden = true;
        this.pagination.hidden = true;
        this.status.textContent = error.message === 'limit'
          ? 'This category is too large for these filters. Standard collection results are shown below; your search and custom filters are not applied.'
          : error.message === 'changed'
            ? 'The listings changed while loading. Standard collection results are shown below. Try again to apply your search and custom filters.'
            : 'Your search and custom filters could not be applied. Standard collection results are shown below. Try again to apply your filters.';
        this.empty.hidden = true;
        this.retry.hidden = error.message === 'limit';
        if (this.facetLoad) { this.facetLoad.disabled = false; this.facetLoad.hidden = false; }
        if (this.facetStatus) this.facetStatus.textContent = 'Custom filters are unavailable. Standard collection results are shown.';
      }
    }
    saveURL() {
      const url = new URL(window.location.href);
      removeAvailability(url);
      FILTER_KEYS.forEach((key) => { url.searchParams.delete(`rh_f_${key}`); (this.filters[key] || []).forEach((value) => url.searchParams.append(`rh_f_${key}`, value)); });
      url.searchParams.delete('page');
      if (this.sort?.value && this.sort.value !== 'recommended') url.searchParams.set('rh_sort', this.sort.value); else url.searchParams.delete('rh_sort');
      if (this.page > 1) url.searchParams.set('rh_page', String(this.page)); else url.searchParams.delete('rh_page');
      window.history.pushState({}, '', url.pathname + url.search);
    }
    facetValues(item, key) {
      const value = item.facets?.[key];
      const values = Array.isArray(value) ? value : typeof value === 'string' ? value.split(/\r?\n/) : [];
      return [...new Map(values.map((part) => {
        const label = String(typeof part === 'object' && part ? part.label || part.title || part.name || '' : part || '').trim();
        return [normalize(label), label];
      }).filter(([key]) => key)).entries()];
    }
    baseMatches(item) {
      const city = cityKey(this.city);
      if (city && cityKey(item.city) !== city) return false;
      if (this.module && moduleKey(item.moduleKey || item.module) !== this.module) return false;
      const terms = normalize(this.query).split(' ').filter(Boolean);
      const haystack = normalize([item.title, item.city, item.address, item.summary, item.module].join(' '));
      return terms.every((term) => haystack.includes(term));
    }
    facetMatches(item, except = '') {
      return FILTER_KEYS.every((key) => {
        const selected = this.filters[key] || [];
        if (key === except || !selected.length) return true;
        const values = this.facetValues(item, key).map(([value]) => value);
        return selected.some((value) => values.includes(value));
      });
    }
    facetDefinitions() {
      let profile = this.module || (this.root.dataset.filterProfile !== 'auto' ? this.root.dataset.filterProfile : '');
      if (!profile) {
        const inferred = moduleKey(this.root.dataset.collectionHandle);
        if (moduleNames[inferred]) profile = inferred;
        else {
          const modules = [...new Set((this.records || []).map((item) => moduleKey(item.moduleKey || item.module)))];
          profile = modules.length === 1 ? modules[0] : 'general';
        }
      }
      const fields = profile === 'trip' ? ['duration', 'inclusions', 'amenities'] : profile === 'virtual-office' ? ['property_type', 'highlights', 'amenities'] : ['property_type', 'amenities'];
      const defs = new Map(fields.map((key) => [key, { key, label: FILTER_LABELS[key] }]));
      this.extraFacets.forEach((item) => defs.set(item.key, { key: item.key, label: item.label || FILTER_LABELS[item.key] }));
      FILTER_KEYS.filter((key) => this.filters[key]?.length).forEach((key) => { if (!defs.has(key)) defs.set(key, { key, label: FILTER_LABELS[key] }); });
      return [...defs.values()];
    }
    renderFacets() {
      if (!this.facetGroups || !this.facetsRequested) return;
      if (!this.complete) {
        if (this.facetStatus) this.facetStatus.textContent = `Checking ${this.records.length} of ${this.expectedTotal} listings for all available options…`;
        return;
      }
      const focus = document.activeElement;
      const focusKey = focus?.dataset?.facetKey;
      const focusValue = focus?.value;
      this.facetGroups.querySelectorAll('details[data-field]').forEach((details) => { if (details.open) this.openFacets.add(details.dataset.field); else this.openFacets.delete(details.dataset.field); });
      const wasRendered = this.facetGroups.childElementCount > 0;
      const fragment = document.createDocumentFragment();
      let groups = 0;
      const base = this.records.filter((item) => this.baseMatches(item));
      this.facetDefinitions().forEach(({ key, label }) => {
        const values = new Map();
        base.forEach((item) => this.facetValues(item, key).forEach(([value, text]) => { if (!values.has(value)) values.set(value, { label: text, count: 0 }); }));
        base.filter((item) => this.facetMatches(item, key)).forEach((item) => this.facetValues(item, key).forEach(([value]) => { values.get(value).count += 1; }));
        (this.filters[key] || []).forEach((value) => { if (!values.has(value)) values.set(value, { label: value, count: 0 }); });
        if (!values.size) return;
        groups += 1;
        const details = element('details', 'collection-facet');
        details.dataset.field = key;
        details.open = wasRendered ? this.openFacets.has(key) : groups <= 2;
        details.append(element('summary', '', label));
        const options = element('div', 'collection-facet__options');
        [...values].sort((a, b) => a[1].label.localeCompare(b[1].label, undefined, { numeric: true })).forEach(([value, option]) => {
          const row = element('label', 'directory-checkbox');
          const input = element('input'); input.type = 'checkbox'; input.value = value; input.dataset.facetKey = key;
          input.checked = (this.filters[key] || []).includes(value); input.disabled = !input.checked && option.count === 0;
          row.append(input, element('span', '', option.label), element('small', '', option.count)); options.append(row);
        });
        details.append(options); fragment.append(details);
      });
      this.facetGroups.replaceChildren(fragment);
      if (focusKey) [...this.facetGroups.querySelectorAll('input')].find((input) => input.dataset.facetKey === focusKey && input.value === focusValue)?.focus({ preventScroll: true });
      this.facetStatus.textContent = groups ? '' : 'No additional filters are available for these listings.';
      if (this.facetLoad) { this.facetLoad.hidden = true; this.facetLoad.disabled = false; }
      const hasFilters = Object.values(this.filters).some((values) => values.length);
      if (this.facetReset) this.facetReset.hidden = !hasFilters;
      if (this.selectedWrap) {
        this.selectedWrap.replaceChildren(); this.selectedWrap.hidden = !hasFilters;
        FILTER_KEYS.forEach((key) => (this.filters[key] || []).forEach((value) => {
          const label = this.records.flatMap((item) => this.facetValues(item, key)).find(([keyValue]) => keyValue === value)?.[1] || value;
          const button = element('button', '', label); button.type = 'button'; button.dataset.removeFacet = key; button.dataset.value = value;
          button.setAttribute('aria-label', `Remove ${FILTER_LABELS[key]}: ${label}`); button.append(element('span', '', '×')); this.selectedWrap.append(button);
        }));
      }
    }
    render() {
      if (!Array.isArray(this.records)) return;
      const results = this.records.filter((item) => this.baseMatches(item) && this.facetMatches(item));
      this.renderFacets();
      this.matchCount = results.length;
      let sort = this.sort.value;
      if (sort === 'title-ascending' || sort === 'title-descending') results.sort((a, b) => String(a.title).localeCompare(String(b.title), undefined, { numeric: true, sensitivity: 'base' }) * (sort === 'title-ascending' ? 1 : -1));
      if (sort === 'created-descending' || sort === 'created-ascending') results.sort((a, b) => ((Number(a.created) || 0) - (Number(b.created) || 0)) * (sort === 'created-ascending' ? 1 : -1));
      const numericPrices = results.filter((item) => typeof item.priceValue === 'number' && Number.isFinite(item.priceValue));
      const priceUnits = new Set(numericPrices.map((item) => `${item.priceCurrency || ''}|${item.priceBasis || ''}`));
      const comparablePrices = this.complete && priceUnits.size <= 1 && numericPrices.length > 0;
      this.sort.querySelectorAll('[value^="price-"]').forEach((option) => { option.disabled = !comparablePrices; });
      if (this.complete && sort.startsWith('price-') && !comparablePrices) {
        sort = 'recommended'; this.sort.value = sort;
        const url = new URL(window.location.href); url.searchParams.delete('rh_sort');
        window.history.replaceState({}, '', url.pathname + url.search);
      }
      if ((sort === 'price-ascending' || sort === 'price-descending') && comparablePrices) results.sort((a, b) => {
        const aKnown = typeof a.priceValue === 'number' && Number.isFinite(a.priceValue);
        const bKnown = typeof b.priceValue === 'number' && Number.isFinite(b.priceValue);
        if (!aKnown || !bKnown) return Number(bKnown) - Number(aKnown);
        return (a.priceValue - b.priceValue) * (sort === 'price-ascending' ? 1 : -1);
      });
      const pages = Math.max(1, Math.ceil(results.length / this.pageSize));
      if (this.complete) this.page = Math.max(1, Math.min(this.page, pages));
      const start = (this.page - 1) * this.pageSize;
      const visible = results.slice(start, start + this.pageSize);
      const visibleKeys = visible.map((item) => item.id).join('|');
      if (visibleKeys !== this.visibleKeys) {
        const fragment = document.createDocumentFragment();
        visible.forEach((item, index) => {
          const node = card(item, this.root, index < 4);
          if (node) fragment.append(node);
        });
        this.grid.replaceChildren(fragment);
        this.visibleKeys = visibleKeys;
      }
      const place = this.city ? ` in ${this.city}` : '';
      const keyword = this.query ? ` matching “${this.query}”` : '';
      const countLabel = `${results.length} ${results.length === 1 ? 'listing' : 'listings'}${place}${keyword}`;
      this.status.textContent = this.complete ? countLabel : `${countLabel} found so far · Checking ${this.records.length} of ${this.expectedTotal} listings…`;
      this.empty.hidden = !this.complete || results.length !== 0;
      this.sortWrap.hidden = results.length < 2;
      this.pagination.replaceChildren();
      this.pagination.hidden = pages <= 1 || !this.complete;
      if (pages > 1) {
        const previous = element('button', '', 'Previous');
        previous.type = 'button';
        previous.dataset.cityPage = String(this.page - 1);
        previous.disabled = this.page === 1;
        const info = element('span', 'directory-pagination__info', `Page ${this.page} of ${pages}`);
        const next = element('button', '', 'Next');
        next.type = 'button';
        next.dataset.cityPage = String(this.page + 1);
        next.disabled = this.page === pages;
        this.pagination.append(previous, info, next);
      }
    }
  }

  let active = null;
  function initialize() {
    const root = document.querySelector('[data-city-results]');
    if (root) {
      try {
        const blocked = JSON.parse(root.querySelector('[data-ignored-native-filters]')?.textContent || '[]');
        blocked.forEach((name) => { if (typeof name === 'string') ignoredNativeFilters.add(name.toLowerCase()); });
      } catch { /* The two standard availability parameters are always excluded. */ }
      const cleanURL = new URL(window.location.href);
      if (removeAvailability(cleanURL)) {
        // Shopify has already filtered the server response: request the clean collection once.
        cleanURL.searchParams.delete('page');
        cleanURL.searchParams.delete('rh_page');
        window.location.replace(cleanURL.href);
        return;
      }
    }
    if (active?.root === root) return;
    active?.dispose();
    active = root ? new CityResults(root) : null;
  }
  document.addEventListener('submit', (event) => {
    const nativeForm = event.target.closest('[data-native-filter-form]');
    if (nativeForm) {
      const params = new URLSearchParams(window.location.search);
      nativeForm.querySelectorAll('[data-preserved-param]').forEach((field) => field.remove());
      nativeForm.querySelectorAll('[name]').forEach((field) => { if (isAvailabilityParam(field.name)) field.disabled = true; });
      params.forEach((value, name) => {
        if (!(name.startsWith('rh_') && name !== 'rh_page') && name !== 'preview_theme_id') return;
        const field = element('input'); field.type = 'hidden'; field.name = name; field.value = value; field.dataset.preservedParam = ''; nativeForm.append(field);
      });
      return;
    }
    const form = event.target.closest('[data-city-finder-form]');
    if (!form) return;
    const values = new FormData(form);
    const category = String(values.get('rh_category') || form.action || '');
    const url = safeURL(category, true);
    if (!url) return;
    event.preventDefault();
    // Keep category parameters (including rh_module) supplied by the form.
    if (active && active.collectionURL?.pathname === url.pathname) {
      const current = new URLSearchParams(window.location.search);
      current.forEach((value, name) => { if (isAvailabilityParam(name)) return; if (name.startsWith('rh_f_') || name.startsWith('filter.')) url.searchParams.append(name, value); else if (['rh_sort', 'sort_by', 'preview_theme_id'].includes(name)) url.searchParams.set(name, value); });
    }
    removeAvailability(url);
    ['page', 'view', 'rh_city', 'rh_q', 'rh_page'].forEach((name) => url.searchParams.delete(name));
    const selectedModule = String(values.get('rh_module') || '').trim();
    if (selectedModule) url.searchParams.set('rh_module', moduleKey(selectedModule));
    const city = String(values.get('rh_city') || '').trim();
    const query = String(values.get('rh_q') || '').trim();
    if (city) url.searchParams.set('rh_city', city);
    if (query) url.searchParams.set('rh_q', query);
    if (active && active.collectionURL?.pathname === url.pathname) {
      window.history.pushState({}, '', url.pathname + url.search);
      active.sync();
    } else {
      window.location.assign(url.href);
    }
  });
  window.addEventListener('popstate', () => active?.sync());
  document.addEventListener('shopify:section:load', initialize);
  document.addEventListener('shopify:section:unload', (event) => {
    if (active && event.target.contains(active.root)) { active.dispose(); active = null; }
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
  else initialize();
})();
